import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Send, Mail, Phone, MapPin, Loader2, CheckCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// API Endpoint - Points to PHP backend
const API_ENDPOINT = '/api/contact.php';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  company: z.string().min(2, 'Company name is required'),
  product_interest: z.string().min(1, 'Please select a product category'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

const productCategories = [
  "TVs & LED Panels",
  "Mobile Phones & Tablets",
  "Laptops & Computers",
  "Gaming Consoles (PlayStation)",
  "Printers & Imaging",
  "IT Accessories (Hard Drives, RAM)",
  "Audio (JBL, Speakers)",
  "Home Appliances (Dyson)",
  "Multiple Categories",
  "Other"
];

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      product_interest: '',
      message: '',
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Submit to PHP backend
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company,
          product_interest: data.product_interest,
          message: data.message,
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Inquiry submitted successfully! We will contact you soon.');
        setIsSubmitted(true);
        form.reset();
        // Reset success state after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or email us directly at info.ajktv@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-32 bg-brand-surface/30" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-16"
        >
          <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-brand-accent/20 text-brand-accent text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            Get In Touch
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
            Request a <span className="gradient-text">Quote</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg px-4">
            Interested in our products? Fill out the form and our team will get back to you within 24 hours
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <h3 className="font-outfit text-xl font-semibold text-white mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-brand-accent" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Email Us</div>
                    <a href="mailto:info.ajktv@gmail.com" className="text-white hover:text-brand-accent transition-colors">
                      info.ajktv@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-brand-accent" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Head Office</div>
                    <address className="text-white not-italic">
                      109/2/1A, Street No. 14A,<br />
                      West Azad Nagar, Krishna Nagar,<br />
                      New Delhi -110051, India
                    </address>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-brand-accent" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Offices</div>
                    <div className="text-white">India • Hong Kong • UAE</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Response */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-primary/20 to-brand-accent/10 border border-white/5">
              <h4 className="font-outfit font-semibold text-white mb-2">Quick Response Guaranteed</h4>
              <p className="text-sm text-gray-400">
                Our team typically responds within 24 hours. For urgent inquiries, please mention "URGENT" in your message.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <div className="p-6 md:p-8 rounded-2xl bg-brand-surface/70 border border-white/5">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-400 max-w-md">
                    Your inquiry has been submitted successfully. Our team will review your request and get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="mt-6 border-brand-primary text-white hover:bg-brand-primary/20"
                  >
                    Submit Another Inquiry
                  </Button>
                </motion.div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-300">Full Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="John Doe" 
                                className="bg-brand-dark/50 border-white/10 text-white placeholder:text-gray-500 focus:border-brand-primary"
                                data-testid="input-name"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-300">Email Address</FormLabel>
                            <FormControl>
                              <Input 
                                type="email"
                                placeholder="john@company.com" 
                                className="bg-brand-dark/50 border-white/10 text-white placeholder:text-gray-500 focus:border-brand-primary"
                                data-testid="input-email"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-300">Phone Number</FormLabel>
                            <FormControl>
                              <Input 
                                type="tel"
                                placeholder="+91 9876543210" 
                                className="bg-brand-dark/50 border-white/10 text-white placeholder:text-gray-500 focus:border-brand-primary"
                                data-testid="input-phone"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-300">Company Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Your Company Ltd." 
                                className="bg-brand-dark/50 border-white/10 text-white placeholder:text-gray-500 focus:border-brand-primary"
                                data-testid="input-company"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="product_interest"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">Product Interest</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger 
                                className="bg-brand-dark/50 border-white/10 text-white"
                                data-testid="select-product"
                              >
                                <SelectValue placeholder="Select product category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-brand-surface border-white/10">
                              {productCategories.map((category) => (
                                <SelectItem 
                                  key={category} 
                                  value={category}
                                  className="text-white hover:bg-brand-primary/20"
                                >
                                  {category}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">Message</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us about your requirements, quantities needed, and any specific questions..."
                              className="bg-brand-dark/50 border-white/10 text-white placeholder:text-gray-500 focus:border-brand-primary min-h-[120px]"
                              data-testid="input-message"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-brand-accent hover:bg-brand-accent/90 text-brand-dark font-semibold py-6 text-lg rounded-xl"
                      data-testid="submit-inquiry-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Inquiry
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
